import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { Alert, Box, IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import PaletteIcon from "@mui/icons-material/Palette";
import CheckIcon from "@mui/icons-material/Check";
import CssBaseline from "@mui/material/CssBaseline";
import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { msalConfig } from "./config/authConfig";
import LoginForm from "./pages/LoginForm";
import AdminPage from "./pages/AdminPage";
import AdminReports from "./pages/AdminReports";
import AdminSubmissions from "./pages/AdminSubmissions";
import AdminUsers from "./pages/AdminUsers";
import AdminAuthorizedSigner from "./pages/AdminAuthorizedSigner";
import DescriptionRequest from "./pages/DescriptionRequest";
import AdminAccessLevelsTest from "./pages/AdminAccessLevelsTest";
import AdminAgreementRecipients from "./pages/AdminAgreementRecipients";
import ArchiveProcess from "./pages/ArchiveProcess";
import SubmissionPeriodConfig from "./pages/SubmissionPeriodConfig";
import BlastEmail from "./pages/BlastEmail";
import PreliminaryNotification from "./pages/PreliminaryNotification";
import HomePage from "./pages/HomePage";
import Unauthorized from "./pages/Unauthorized";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminLayout from "./components/AdminLayout";
import { AuthProvider } from "./context/AuthContext";
import { logError } from "./utils/logger";
import { colors } from "./utils/styles";

const msalInstance = new PublicClientApplication(msalConfig);

const getInitialBgColor = () => {
  const savedColor = localStorage.getItem("dev-bg-color");
  return savedColor || "#e3f2fd"; // Light blue default
};

const theme = createTheme({
  palette: {
    primary: {
      main: colors.navy
    },
    secondary: {
      main: colors.goldDark
    }
  },
  typography: {
    fontFamily: "'DM Sans', sans-serif"
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        "input, select, textarea, button": {
          font: "inherit"
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            minHeight: 42,
            fontSize: 14,
            color: colors.text,
            background: colors.white,
            borderRadius: "6px",
            padding: "0 14px",
            "& fieldset": { borderColor: colors.border },
            "&:hover fieldset": { border: `1.5px solid ${colors.border}` },
            "&.Mui-focused fieldset": { border: `1.5px solid ${colors.navy}bb` }
          },
          "& .MuiOutlinedInput-input": { padding: "10px 0 !important" }
        }
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root.MuiAutocomplete-inputRoot": {
            minHeight: 42,
            padding: "0 14px"
          },
          "& .MuiAutocomplete-input": { padding: "10px 0 !important" }
        }
      }
    },
    MuiPickersInputBase: {
      styleOverrides: {
        root: {
          minHeight: 42,
          fontSize: 14,
          color: colors.text,
          background: colors.white,
          borderRadius: "6px !important",
          "& fieldset": { borderColor: `${colors.border}` },
          "&:hover .MuiPickersOutlinedInput-notchedOutline": {
            border: `1.5px solid ${colors.border} !important`
          },
          "&.Mui-focused .MuiPickersOutlinedInput-notchedOutline": {
            border: `1.5px solid ${colors.navy}bb  !important`
          }
        },
        sectionsContainer: { padding: "10px 0 !important" }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 6, textTransform: "none" }
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: colors.navy,
          padding: "2px 4px",
          "&.Mui-checked": { color: colors.navy }
        }
      }
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: { gap: 4, marginLeft: 0, marginRight: 0 },
        label: { fontSize: 14, color: colors.text }
      }
    }
  }
});

function App() {
  const [isReady, setIsReady] = useState(false);
  const [bgColor, setBgColor] = useState(getInitialBgColor);
  const [colorMenuAnchor, setColorMenuAnchor] = useState(null);

  useEffect(() => {
    msalInstance
      .initialize()
      .then(() => {
        return msalInstance.handleRedirectPromise();
      })
      .then(() => {
        setIsReady(true);
      })
      .catch((error) => {
        logError("MSAL initialization failed:", error);
        setIsReady(true);
      });
  }, []);

  if (!isReady) return null;

  const handleColorChange = (color) => {
    setBgColor(color);
    localStorage.setItem("dev-bg-color", color);
    setColorMenuAnchor(null);
  };

  const colorOptions = [
    { name: "Light Blue", value: "#e3f2fd" },
    { name: "Light Green", value: "#e8f5e9" },
    { name: "Light Yellow", value: "#fffde7" },
    { name: "Light Pink", value: "#fce4ec" },
    { name: "Light Purple", value: "#f3e5f5" },
    { name: "Light Orange", value: "#fff3e0" },
    { name: "Light Gray", value: "#f5f5f5" },
    { name: "White", value: "#ffffff" }
  ];

  // Detect environment based on API URL
  const apiRoot = process.env.REACT_APP_API_ROOT || "";
  const isProduction = apiRoot.includes("directchargeapi.azurewebsites.net");

  const getEnvironmentInfo = () => {
    if (isProduction) return null;
    return { name: "DEVELOPMENT", color: "info" };
  };

  const envInfo = getEnvironmentInfo();

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <MsalProvider instance={msalInstance}>
        <Router>
          <AuthProvider>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                minHeight: "100vh",
                bgcolor: envInfo ? bgColor : "inherit" // Custom background for development
              }}
            >
              {envInfo && (
                <Alert
                  severity="info"
                  icon={false}
                  sx={{
                    borderRadius: 0,
                    position: "sticky",
                    top: 0,
                    zIndex: 1300,
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: "0.875rem",
                    bgcolor: bgColor,
                    color: "#1a2744",
                    "& .MuiAlert-message": {
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      justifyContent: "center"
                    }
                  }}
                >
                  🚧 {envInfo.name} ENVIRONMENT 🚧
                  <IconButton
                    onClick={(e) => setColorMenuAnchor(e.currentTarget)}
                    size="small"
                    sx={{
                      ml: 2,
                      color: "#1a2744",
                      "&:hover": { bgcolor: "rgba(0,0,0,0.08)" }
                    }}
                    title="Change background color"
                  >
                    <PaletteIcon fontSize="small" />
                  </IconButton>
                </Alert>
              )}
              <Menu
                anchorEl={colorMenuAnchor}
                open={Boolean(colorMenuAnchor)}
                onClose={() => setColorMenuAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                {colorOptions.map((option) => (
                  <MenuItem
                    key={option.value}
                    onClick={() => handleColorChange(option.value)}
                    selected={bgColor === option.value}
                  >
                    <ListItemIcon>{bgColor === option.value && <CheckIcon fontSize="small" />}</ListItemIcon>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        bgcolor: option.value,
                        border: "1px solid #ccc",
                        borderRadius: 1,
                        mr: 1
                      }}
                    />
                    <ListItemText>{option.name}</ListItemText>
                  </MenuItem>
                ))}
              </Menu>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/preliminary-notification" element={<PreliminaryNotification />} />
                <Route path="/login" element={<LoginForm />} />
                <Route path="/unauthorized" element={<Unauthorized />} />
                <Route element={<ProtectedRoute />}>
                  <Route element={<AdminLayout />}>
                    <Route path="/admin" element={<AdminPage />} />
                    <Route path="/admin/reports" element={<AdminReports />} />
                    <Route path="/admin/submissions" element={<AdminSubmissions />} />
                    <Route path="/admin/users" element={<AdminUsers />} />
                    <Route path="/admin/authorized-signer" element={<AdminAuthorizedSigner />} />
                    <Route path="/admin/description-request" element={<DescriptionRequest />} />
                    <Route path="/admin/access-levels-test" element={<AdminAccessLevelsTest />} />
                    <Route path="/admin/agreement-recipients" element={<AdminAgreementRecipients />} />
                    <Route path="/admin/archive-process" element={<ArchiveProcess />} />
                    <Route path="/admin/submission-period" element={<SubmissionPeriodConfig />} />
                    <Route path="/admin/blast-email" element={<BlastEmail />} />
                  </Route>
                </Route>
              </Routes>
            </Box>
          </AuthProvider>
        </Router>
      </MsalProvider>
    </ThemeProvider>
  );
}

export default App;
